import type { AuthResponse, AuthSession, LogoutReason, VerificationFlow } from './types';

const SESSION_KEY = 'trini_wallet_session_v1';
const FLOW_KEY = 'trini_wallet_verification_v1';
const LOGOUT_REASON_KEY = 'trini_wallet_logout_reason_v1';
const SESSION_EVENT = 'trini-wallet-session-change';

const parseExpiresAt = (value: string | number | undefined): number | undefined => {
  if (value === undefined) return undefined;
  if (typeof value === 'number') return value < 10_000_000_000 ? value * 1000 : value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const parseJson = <T>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const emitSessionChange = (): void => { window.dispatchEvent(new Event(SESSION_EVENT)); };

const rememberLogoutReason = (reason: LogoutReason): void => {
  if (reason === 'manual') sessionStorage.removeItem(LOGOUT_REASON_KEY);
  else sessionStorage.setItem(LOGOUT_REASON_KEY, reason);
};

export const createSession = (response: AuthResponse, fallbackEmail: string): AuthSession => ({
  accessToken: response.access_token,
  expiresAt: parseExpiresAt(response.access_token_expires_at),
  user: response.user ?? { email: response.email ?? fallbackEmail },
});

export const isAccessTokenExpired = (session: AuthSession, now = Date.now()): boolean =>
  session.expiresAt !== undefined && session.expiresAt <= now;

/** Persist only the web session fields, including when reading an older stored session. */
const webSession = ({ accessToken, expiresAt, user }: AuthSession): AuthSession => ({ accessToken, expiresAt, user });

export const saveSession = (session: AuthSession): void => {
  localStorage.setItem(SESSION_KEY, JSON.stringify(webSession(session)));
  emitSessionChange();
};

export const readSession = (): AuthSession | null => {
  const stored = localStorage.getItem(SESSION_KEY);
  const session = parseJson<AuthSession>(stored);
  if (!session?.accessToken || !session.user?.email) return null;
  if (isAccessTokenExpired(session)) {
    localStorage.removeItem(SESSION_KEY);
    rememberLogoutReason('expired');
    return null;
  }
  const current = webSession(session);
  const serialized = JSON.stringify(current);
  if (serialized !== stored) localStorage.setItem(SESSION_KEY, serialized);
  return current;
};

export const clearSession = (reason: LogoutReason = 'manual'): void => {
  localStorage.removeItem(SESSION_KEY);
  rememberLogoutReason(reason);
  emitSessionChange();
};

/** Reads and forgets why the last session ended, so the notice shows once. */
export const consumeLogoutReason = (): Exclude<LogoutReason, 'manual'> | null => {
  const reason = sessionStorage.getItem(LOGOUT_REASON_KEY);
  sessionStorage.removeItem(LOGOUT_REASON_KEY);
  return reason === 'expired' || reason === 'revoked' ? reason : null;
};

export const saveVerificationFlow = (flow: VerificationFlow): void =>
  sessionStorage.setItem(FLOW_KEY, JSON.stringify(flow));

export const readVerificationFlow = (): VerificationFlow | null => {
  const flow = parseJson<VerificationFlow>(sessionStorage.getItem(FLOW_KEY));
  if (!flow || !flow.email || !['login', 'registration'].includes(flow.mode)) return null;
  if (flow.mode === 'login' && !flow.nonce) return null;
  return flow;
};

export const clearVerificationFlow = (): void => sessionStorage.removeItem(FLOW_KEY);

export const sessionEventName = SESSION_EVENT;
